import { useCallback, useSyncExternalStore } from "react";

/**
 * localStorage as a shared, reactive store — one value per key, seen the same
 * way by every component holding it.
 *
 * 7HAI-1094: this used to be a private `useState` per hook instance, seeded from
 * localStorage in the initialiser and never re-read. A write updated storage and
 * the state of the instance that wrote; every other instance kept the copy it had
 * read at its own mount. Two components could therefore hold different values for
 * one key indefinitely, and two of them did: accepting the privacy policy left
 * AgreementsPage with the accepted user and ProtectedRoute with the pending one,
 * so the page redirected to the dashboard, the guard redirected back, and React —
 * both redirects being effects — gave up with "Maximum update depth exceeded" and
 * unmounted the app. The user was left on a blank page, locked out on their first
 * login, with the acceptance already recorded server-side.
 *
 * A key is one value. The subscriber registry below is what makes that true in the
 * client too: a write notifies every instance of that key and they all re-read.
 * The `storage` event does the same across tabs, so signing out in one tab no
 * longer leaves another holding a live session.
 *
 * The returned tuple is unchanged — `[value, setValue, removeValue]` — so callers
 * did not have to move.
 */

/** Per-key subscribers. Notified on every write, removal and cross-tab change. */
const subscribers = new Map<string, Set<() => void>>();

/**
 * Parsed values, keyed by the raw string they came from.
 *
 * `useSyncExternalStore` demands a snapshot that is referentially stable between
 * writes: returning a freshly-parsed object on every call would make React see an
 * endless stream of changes and re-render forever — the very failure this hook
 * exists to stop, reintroduced one level down. Caching the parse against the exact
 * raw string keeps the reference stable while staying correct, since any change to
 * the stored string invalidates it.
 */
const parsed = new Map<string, { raw: string; value: unknown }>();

let listeningForOtherTabs = false;

/** Re-read on the next snapshot, then wake everyone holding this key. */
const invalidate = (keyName: string): void => {
  parsed.delete(keyName);
  subscribers.get(keyName)?.forEach((notify) => notify());
};

const listenForOtherTabs = (): void => {
  if (listeningForOtherTabs || typeof window === "undefined") return;
  listeningForOtherTabs = true;
  window.addEventListener("storage", (event: StorageEvent) => {
    // A null key means the other tab called clear(); every key is suspect.
    if (event.key === null) {
      parsed.clear();
      subscribers.forEach((set) => set.forEach((notify) => notify()));
      return;
    }
    invalidate(event.key);
  });
};

const subscribe = (keyName: string, notify: () => void): (() => void) => {
  listenForOtherTabs();
  let set = subscribers.get(keyName);
  if (!set) {
    set = new Set();
    subscribers.set(keyName, set);
  }
  set.add(notify);
  return () => {
    set.delete(notify);
    if (set.size === 0) subscribers.delete(keyName);
  };
};

const read = <Type>(keyName: string, defaultValue: Type): Type => {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(keyName);
  } catch (err) {
    console.error(`Error accessing localStorage key "${keyName}":`, err);
    return defaultValue;
  }
  // Absent: hand back the caller's own default rather than caching one, so two
  // instances that disagree about the default each keep theirs.
  if (raw === null) return defaultValue;

  const cached = parsed.get(keyName);
  if (cached && cached.raw === raw) return cached.value as Type;

  try {
    const value = JSON.parse(raw) as Type;
    parsed.set(keyName, { raw, value });
    return value;
  } catch (err) {
    console.error(`Error accessing localStorage key "${keyName}":`, err);
    return defaultValue;
  }
};

export function useLocalStorage<Type>(
  keyName: string,
  defaultValue: Type,
): [Type, (newValue: Type) => void, () => void] {
  const storedValue = useSyncExternalStore(
    useCallback((notify: () => void) => subscribe(keyName, notify), [keyName]),
    () => read(keyName, defaultValue),
    () => defaultValue,
  );

  const setValue = useCallback(
    (newValue: Type) => {
      try {
        window.localStorage.setItem(keyName, JSON.stringify(newValue));
        invalidate(keyName);
      } catch (err) {
        console.error(`Error setting localStorage key "${keyName}":`, err);
      }
    },
    [keyName],
  );

  const removeValue = useCallback(() => {
    try {
      window.localStorage.removeItem(keyName);
      invalidate(keyName);
    } catch (err) {
      console.error(`Error removing localStorage key "${keyName}":`, err);
    }
  }, [keyName]);

  return [storedValue, setValue, removeValue];
}
